import { useMemo } from "react";
import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import WorkflowScopeTableFindingCell from "./components/WorkflowScopeTableFindingCell";
import WorkflowTableTextCell from "../cells/WorkflowTableTextCell";
import useWorkflowDetail from "../../hooks/useWorkflowDetail";
import WorkflowFlatTable from "../WorkflowFlatTable";
import { WorkflowScopeTableRiskCell } from "./components/WorkflowScopeTableRiskCell";

export type WorkflowScopeData = ReturnType<
  typeof useWorkflowDetail
>["scopeData"];

interface WorkflowScopeTableProps {
  loading: boolean;
  scopeData: WorkflowScopeData;
  onFindingClick?: () => void;
}

export default function WorkflowScopeTable({
  loading,
  scopeData,
  onFindingClick,
}: WorkflowScopeTableProps) {
  const { listItems = [], space } = scopeData;
  const rows = useMemo(() => {
    return listItems?.map((finding) => {
      // do we need to handle more types?
      const routeParam = finding.findingMrn.includes("/advisories/")
        ? "advisory"
        : "cve";
      const findingId = finding.findingMrn.split("/").pop();

      return {
        mrn: finding.findingMrn,
        rating: finding.rating,
        finding: finding.title,
        assets: finding.assetsCount,
        method: finding.method,
        url: `/space/vulns/${routeParam}/${findingId}?spaceId=${space?.id}`,
      };
    });
  }, [listItems, space?.id]);

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 0 0",
        flexDirection: "column",
        alignItems: "flex-start",
        alignSelf: "stretch",
        padding: 3,
        gap: 1.5,
        borderRadius: 2,
        backgroundColor: "background.paper",
        height: 240,
        minHeight: 240,
        overflow: "hidden",
      }}
    >
      <WorkflowFlatTable
        loading={loading}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.mrn}
        onRowClick={onFindingClick}
      />
    </Box>
  );
}

export const columns: GridColDef[] = [
  {
    field: "rating",
    headerName: "Risk Level",
    headerClassName: "left-cell",
    cellClassName: "left-cell",
    width: 90,
    resizable: false,
    headerAlign: "left",
    align: "left",
    renderCell: (params) => <WorkflowScopeTableRiskCell value={params.value} />,
  },
  {
    field: "finding",
    headerName: "finding",
    flex: 1,
    resizable: false,
    renderCell: (params) => (
      <WorkflowScopeTableFindingCell
        value={params.value}
        url={params.row.url}
      />
    ),
  },
  {
    field: "assets",
    headerName: "assets",
    width: 84,
    resizable: false,
    headerAlign: "right",
    align: "right",
    renderCell: (params) => <WorkflowTableTextCell text={params.value} />,
  },
  {
    field: "method",
    headerName: "method",
    headerClassName: "right-cell",
    cellClassName: "right-cell",
    width: 84,
    resizable: false,
    headerAlign: "right",
    align: "right",
    renderCell: (params) => (
      <WorkflowTableTextCell
        text={params.value.toLowerCase()}
        TypographyProps={{
          sx: {
            textTransform: "capitalize",
            fontWeight:
              params.value.toLowerCase() === "selected" ? "bold" : "normal",
          },
        }}
      />
    ),
  },
];
